/**
 * O helicóptero do heliponto: o corpo é um PNG, as quatro pás são desenhadas
 * aqui, a cada frame, no plano do rotor — é o que as deixa rodar.
 *
 * Tem vida própria: de vez em quando liga o rotor e volta a desligá-lo; quando
 * a última pessoa com o cargo "CEO" sai do escritório, levanta voo e vai-se; e
 * quando uma volta a entrar, chega e aterra. Sem ninguém com esse cargo no
 * servidor fica pousado, só com as voltas ao rotor.
 *
 * As contas (o rotor, as fases do voo, a presença do CEO) são puras e estão
 * testadas; a classe só as passa para objectos do Phaser.
 */
import type { Snowflake } from "@greenvoice/shared";
import type PhaserT from "phaser";

import type { AppState } from "../store/store";

// ── Geometria (medida no render de 1536×1024, reduzido a 256 px de largura) ──

/** A caixa opaca do render e a escala para os 256 px do PNG do corpo. */
const CAIXA = { x: 130, y: 86, w: 1242 } as const;
const ESCALA = 256 / CAIXA.w;
const noPng = (x: number, y: number) => ({ x: (x - CAIXA.x) * ESCALA, y: (y - CAIXA.y) * ESCALA });

/**
 * O disco do rotor: centro e dois semi-diâmetros conjugados (a elipse em que o
 * círculo das pás se vê de 3/4). Tirados das quatro pontas das pás do render:
 * o centro é o meio das pontas opostas, `u` vai para a pá de cima-esquerda e
 * `v` para a de cima-direita. Em px do PNG, a partir do canto superior esquerdo.
 */
export const ROTOR = {
  centro: noPng(917, 437.5),
  u: { x: -411.5 * ESCALA, y: -239 * ESCALA },
  v: { x: 432.5 * ESCALA, y: -166 * ESCALA },
  /** A tampa do mastro, que se pinta por cima das pás. */
  tampa: noPng(905, 404),
} as const;

/** Uma pá, do raio `r0` ao `r1` (1 = ponta), com meia-largura `meia`, no ângulo `a`. */
export function pontosDaPa(a: number, r0: number, r1: number, meia: number): { x: number; y: number }[] {
  const c = Math.cos(a);
  const s = Math.sin(a);
  const noPlano: [number, number][] = [
    [r0 * c - meia * s, r0 * s + meia * c],
    [r1 * c - meia * s, r1 * s + meia * c],
    [r1 * c + meia * s, r1 * s - meia * c],
    [r0 * c + meia * s, r0 * s - meia * c],
  ];
  return noPlano.map(([p, q]) => ({
    x: ROTOR.centro.x + p * ROTOR.u.x + q * ROTOR.v.x,
    y: ROTOR.centro.y + p * ROTOR.u.y + q * ROTOR.v.y,
  }));
}

// ── O rotor ────────────────────────────────────────────────────────────────

/** Velocidade de cruzeiro, em rad/s. */
export const ROTOR_MAX = 19;
const ACELERA = 7; // rad/s² a ligar
const TRAVA = 3.5; // rad/s² a desligar — pára mais devagar do que arranca

export interface Rotor {
  angulo: number;
  velocidade: number;
}

/** Um passo do rotor em direcção à velocidade `alvo`, em `dt` segundos. */
export function avancarRotor(rotor: Rotor, alvo: number, dt: number): void {
  const d = alvo - rotor.velocidade;
  const passo = (d > 0 ? ACELERA : TRAVA) * dt;
  rotor.velocidade = Math.abs(d) <= passo ? alvo : rotor.velocidade + Math.sign(d) * passo;
  rotor.angulo = (rotor.angulo + rotor.velocidade * dt) % (Math.PI * 2);
}

// ── O voo ──────────────────────────────────────────────────────────────────

/** Durações das fases, em ms. */
export const VOO = {
  aquecer: 2600, // rotor a ganhar rotação, ainda pousado
  subir: 3000, // sobe a direito por cima do heliponto
  partir: 3400, // afasta-se para nascente e some
  chegar: 3400, // aparece de nascente e pára por cima do heliponto
  pousar: 3000, // desce a direito
} as const;

/** Quanto sobe (px) e para onde vai / de onde vem, relativo ao sítio pousado. */
export const ALTURA_DO_VOO = 70;
export const LONGE = { x: 560, y: -110 } as const;

const suave = (t: number) => {
  const k = Math.min(1, Math.max(0, t));
  return k * k * (3 - 2 * k);
};

export interface Pose {
  dx: number;
  dy: number;
  escala: number;
  alfa: number;
  /** 0 = pousado, 1 = no ar (para a sombra). */
  noAr: number;
}

const POUSADO: Pose = { dx: 0, dy: 0, escala: 1, alfa: 1, noAr: 0 };

/** Onde está o helicóptero `t` ms depois de começar a descolar. */
export function poseADescolar(t: number): Pose {
  if (t < VOO.aquecer) return POUSADO;
  const subida = suave((t - VOO.aquecer) / VOO.subir);
  const ida = suave((t - VOO.aquecer - VOO.subir) / VOO.partir);
  return {
    dx: LONGE.x * ida,
    dy: -ALTURA_DO_VOO * subida + LONGE.y * ida,
    escala: 1 + 0.12 * subida + 0.18 * ida,
    alfa: 1 - suave((ida - 0.45) / 0.55),
    noAr: subida,
  };
}

/** O mesmo, a aterrar: `t` ms depois de começar a chegar. */
export function poseAAterrar(t: number): Pose {
  const vinda = suave(t / VOO.chegar);
  const descida = suave((t - VOO.chegar) / VOO.pousar);
  const longe = 1 - vinda;
  return {
    dx: LONGE.x * longe,
    dy: -ALTURA_DO_VOO * (1 - descida) + LONGE.y * longe,
    escala: 1 + 0.12 * (1 - descida) + 0.18 * longe,
    alfa: suave(vinda / 0.55),
    noAr: 1 - descida,
  };
}

export const DURACAO_DESCOLAR = VOO.aquecer + VOO.subir + VOO.partir;
export const DURACAO_ATERRAR = VOO.chegar + VOO.pousar;

// ── Quem é o CEO ───────────────────────────────────────────────────────────

/**
 * Há alguém com o cargo "CEO" no escritório agora?
 *
 * `null` quando no servidor não há cargo com esse nome, ou ninguém o tem (ou
 * os membros ainda não chegaram): aí o helicóptero não tem dono e fica pousado.
 * O próprio jogador conta — está no escritório enquanto esta cena corre — e
 * não vem em `positions` (ver o `SpaceState` da store).
 */
export function ceoPresente(
  estado: Pick<AppState, "roles" | "members" | "positions" | "self">,
  serverId: Snowflake,
): boolean | null {
  const cargos = new Set(
    Object.values(estado.roles)
      .filter((r) => r.serverId === serverId && r.name.trim().toLowerCase() === "ceo")
      .map((r) => r.id),
  );
  if (cargos.size === 0) return null;
  const eCeo = (userId: Snowflake) =>
    estado.members[`${serverId}:${userId}`]?.roleIds.some((id) => cargos.has(id)) ?? false;
  const temCeo = Object.values(estado.members).some(
    (m) => m.serverId === serverId && m.roleIds.some((id) => cargos.has(id)),
  );
  if (!temCeo) return null;
  if (estado.self && eCeo(estado.self.id)) return true;
  return Object.keys(estado.positions[serverId] ?? {}).some(eCeo);
}

// ── A peça no mundo ────────────────────────────────────────────────────────

/** O helicóptero de cada cena, para o ciclo de `update` o encontrar. */
const porCena = new WeakMap<PhaserT.Scene, Helicoptero>();

export function helicopteroDaCena(scene: PhaserT.Scene): Helicoptero | undefined {
  return porCena.get(scene);
}

type Estado = "pousado" | "a_descolar" | "fora" | "a_aterrar";

/** Quanto tempo a presença tem de se manter antes de o helicóptero reagir. */
const ESPERA_SAIDA_MS = 4000;
const ESPERA_CHEGADA_MS = 1000;
/** Antes disto não se decide nada: a lista de membros ainda pode estar a chegar. */
const ARRANQUE_MS = 2500;
/** A volta ao rotor de vez em quando, pousado. */
const VOLTA_MS = 5500;
const PAUSA_MIN_MS = 35000;
const PAUSA_MAX_MS = 90000;

const PA_R0 = 0.1;
const PA_MEIA = 0.05;
const COR_PA = 0x2b303c;
const COR_CONTORNO = 0x111318;
const COR_PONTA = 0xd9a441;
/** Profundidade no ar: por cima de tudo o que há no andar. */
const PROFUNDIDADE_NO_AR = 1_000_000;

/** Um polígono fechado com o preenchimento e/ou o traço que o `g` tiver definidos. */
function poligono(
  g: PhaserT.GameObjects.Graphics,
  pontos: readonly { x: number; y: number }[],
  encher: boolean,
  tracar: boolean,
): void {
  const [primeiro, ...resto] = pontos;
  if (!primeiro) return;
  g.beginPath();
  g.moveTo(primeiro.x, primeiro.y);
  for (const p of resto) g.lineTo(p.x, p.y);
  g.closePath();
  if (encher) g.fillPath();
  if (tracar) g.strokePath();
}

export class Helicoptero {
  private readonly corpo: PhaserT.GameObjects.Container;
  private readonly rotorGfx: PhaserT.GameObjects.Graphics;
  private readonly sombra: PhaserT.GameObjects.Ellipse;
  private readonly rotor: Rotor = { angulo: 0.35, velocidade: 0 };
  private estado: Estado = "pousado";
  private desde = 0;
  private criadoEm: number | null = null;
  private decidido = false;
  private voltaAte = 0;
  private proximaVolta = 0;
  private presencaVista: boolean | null = null;
  private presencaDesde = 0;

  constructor(
    scene: PhaserT.Scene,
    private readonly x: number,
    private readonly y: number,
    textura: string,
    private readonly profundidade: number,
    sombraBase: { dx: number; dy: number; w: number; h: number },
  ) {
    this.sombra = scene.add
      .ellipse(x + sombraBase.dx, y + sombraBase.dy, sombraBase.w, sombraBase.h, 0x000000, 0.3)
      .setDepth(profundidade - 1);
    const imagem = scene.add.image(0, 0, textura).setOrigin(0, 0);
    this.rotorGfx = scene.add.graphics();
    // O contentor tem a origem no canto do PNG, deslocado para o centro dele:
    // escala a partir do meio do helicóptero, não do canto.
    const meio = { x: imagem.width / 2, y: imagem.height / 2 };
    imagem.setPosition(-meio.x, -meio.y);
    this.rotorGfx.setPosition(-meio.x, -meio.y);
    this.corpo = scene.add.container(x, y, [imagem, this.rotorGfx]).setDepth(profundidade);
    this.desenharRotor();
    porCena.set(scene, this);
  }

  /** Um frame. `presente` é o `ceoPresente` deste instante. */
  update(agora: number, deltaMs: number, presente: boolean | null): void {
    if (this.criadoEm === null) {
      this.criadoEm = agora;
      this.proximaVolta = agora + 8000 + Math.random() * 12000;
    }
    this.lerPresenca(agora, presente);

    let alvo = 0;
    let pose = POUSADO;
    const t = agora - this.desde;
    switch (this.estado) {
      case "pousado":
        if (agora >= this.proximaVolta && this.voltaAte < agora) {
          this.voltaAte = agora + VOLTA_MS;
          this.proximaVolta = agora + VOLTA_MS + PAUSA_MIN_MS + Math.random() * (PAUSA_MAX_MS - PAUSA_MIN_MS);
        }
        alvo = agora < this.voltaAte ? ROTOR_MAX : 0;
        break;
      case "a_descolar":
        alvo = ROTOR_MAX;
        pose = poseADescolar(t);
        if (t >= DURACAO_DESCOLAR) this.mudar("fora", agora);
        break;
      case "fora":
        pose = { ...POUSADO, alfa: 0 };
        this.rotor.velocidade = 0;
        break;
      case "a_aterrar":
        alvo = t < DURACAO_ATERRAR ? ROTOR_MAX : 0;
        pose = poseAAterrar(t);
        if (t >= DURACAO_ATERRAR) this.mudar("pousado", agora);
        break;
    }

    avancarRotor(this.rotor, alvo, deltaMs / 1000);
    this.corpo.setPosition(this.x + pose.dx, this.y + pose.dy);
    this.corpo.setScale(pose.escala);
    this.corpo.setAlpha(pose.alfa);
    this.corpo.setVisible(pose.alfa > 0.01);
    this.corpo.setDepth(pose.noAr > 0.02 ? PROFUNDIDADE_NO_AR : this.profundidade);
    // A sombra fica no heliponto: encolhe e desbota à medida que ele sobe, e
    // desaparece quando ele se afasta.
    const longe = Math.min(1, Math.hypot(pose.dx, pose.dy + ALTURA_DO_VOO * pose.noAr) / 200);
    this.sombra.setScale(1 - 0.35 * pose.noAr, 1 - 0.35 * pose.noAr);
    this.sombra.setAlpha((1 - 0.7 * pose.noAr) * (1 - longe) * (pose.alfa > 0 ? 1 : 0));
    this.desenharRotor();
  }

  private lerPresenca(agora: number, presente: boolean | null): void {
    if (this.criadoEm === null || agora - this.criadoEm < ARRANQUE_MS) return;
    // Sem CEO no servidor, o helicóptero fica em casa.
    const efectiva = presente ?? true;
    if (!this.decidido) {
      // Primeira decisão, sem animação: quem abre o escritório com o CEO fora
      // encontra o heliponto vazio.
      this.decidido = true;
      this.presencaVista = efectiva;
      this.presencaDesde = agora;
      if (!efectiva) this.mudar("fora", agora);
      return;
    }
    if (efectiva !== this.presencaVista) {
      this.presencaVista = efectiva;
      this.presencaDesde = agora;
    }
    const firme = agora - this.presencaDesde >= (efectiva ? ESPERA_CHEGADA_MS : ESPERA_SAIDA_MS);
    if (!firme) return;
    if (!efectiva && this.estado === "pousado") this.mudar("a_descolar", agora);
    if (efectiva && this.estado === "fora") this.mudar("a_aterrar", agora);
  }

  private mudar(estado: Estado, agora: number): void {
    this.estado = estado;
    this.desde = agora;
    this.voltaAte = 0;
  }

  private desenharRotor(): void {
    const g = this.rotorGfx;
    g.clear();
    const q = this.rotor.velocidade / ROTOR_MAX;
    if (q > 0.05) {
      // O disco que as pás desenham a rodar: uma elipse escura, quase transparente.
      const disco = Array.from({ length: 40 }, (_, i) => {
        const a = (i / 40) * Math.PI * 2;
        return {
          x: ROTOR.centro.x + Math.cos(a) * ROTOR.u.x + Math.sin(a) * ROTOR.v.x,
          y: ROTOR.centro.y + Math.cos(a) * ROTOR.u.y + Math.sin(a) * ROTOR.v.y,
        };
      });
      g.fillStyle(0x1c2230, 0.2 * q);
      g.lineStyle(1, COR_PONTA, 0.3 * q);
      poligono(g, disco, true, true);
    }
    // A rodar depressa, as pás esbatem-se.
    const alfa = 1 - 0.7 * q;
    for (let k = 0; k < 4; k += 1) {
      const a = this.rotor.angulo + (k * Math.PI) / 2;
      const pa = pontosDaPa(a, PA_R0, 1, PA_MEIA);
      g.fillStyle(COR_PA, alfa);
      g.lineStyle(1, COR_CONTORNO, alfa);
      poligono(g, pa, true, true);
      g.fillStyle(COR_PONTA, alfa);
      poligono(g, pontosDaPa(a, 0.9, 1, PA_MEIA), true, false);
    }
    // A tampa do mastro, por cima das raízes das pás.
    const { tampa } = ROTOR;
    g.fillStyle(COR_CONTORNO, 1);
    g.fillEllipse(tampa.x, tampa.y, 13, 9);
    g.fillStyle(0x2f3542, 1);
    g.fillEllipse(tampa.x, tampa.y - 0.5, 11, 7);
    g.lineStyle(1, COR_PONTA, 1);
    g.strokeEllipse(tampa.x, tampa.y + 1, 11, 7);
  }
}
